/* eslint-disable no-use-before-define */
import { addRxPlugin, createRxDatabase, removeRxDatabase, RxDocument, RxStorage } from 'rxdb';
import { getRxStorageIndexedDB } from 'rxdb-premium/plugins/storage-indexeddb';
import { getAjv, wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv';
import { RxDBDevModePlugin, disableWarnings } from 'rxdb/plugins/dev-mode';
import { RxDBCleanupPlugin } from 'rxdb/plugins/cleanup';
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election';
import { RxDBLocalDocumentsPlugin } from 'rxdb/plugins/local-documents';
import { RxDBMigrationSchemaPlugin } from 'rxdb/plugins/migration-schema';
import addAjvFormats from 'ajv-formats';
import Bluebird from 'bluebird';

import type { RxNoodleCollections, RxNoodleDB, RxNoodleDBCollectionsKeys } from './rxdb.types';
import { CURRENT_RESET_NUMBER, CURRENT_RXDB_SUFFIX_KEY, IS_DEV_MODE, PREVIOUS_RXDB_PREFIX, RESET_DB_KEY, RXDB_PREFIX } from './rxdb.constants';
import { questionnaireFieldTemplateSchema } from './schemas/questionnaireFieldTemplate.schema';
import { questionnaireTemplateSchema } from './schemas/questionnaireTemplate.schema';
import { qreFieldTemplateSchema } from './schemas/qreFieldTemplate.schema';
import { qreTemplateOfQreSchema } from './schemas/qreTemplateOfQre.schema';
import { rxdbError, rxdbLog } from './rxdb.tools';

if (IS_DEV_MODE) {
  disableWarnings();
  addRxPlugin(RxDBDevModePlugin);
}

addRxPlugin(RxDBLeaderElectionPlugin);
addRxPlugin(RxDBCleanupPlugin);
addRxPlugin(RxDBLocalDocumentsPlugin);
addRxPlugin(RxDBMigrationSchemaPlugin);

if (typeof window !== 'undefined') {
  const ajv = getAjv();
  // @ts-expect-error - Known type mismatch between ajv versions
  addAjvFormats(ajv);
}

function getRxDevStorage(): RxStorage<unknown, unknown> {
  return wrappedValidateAjvStorage({
    storage: getRxStorageIndexedDB(),
  });
}

function getRxProdStorage(): RxStorage<unknown, unknown> {
  return wrappedValidateAjvStorage({
    storage: getRxStorageIndexedDB(),
  });
}

function getRxStorage(): RxStorage<unknown, unknown> {
  return IS_DEV_MODE ? getRxDevStorage() : getRxProdStorage();
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function defaultMigrationStrategy(_doc: RxDocument): RxDocument | null {
  return null;
}

async function initializeCollections(db: RxNoodleDB): Promise<boolean> {
  try {
    await db.addCollections({
      qreFieldTemplate: {
        migrationStrategies: {},
        schema: qreFieldTemplateSchema,
      },
      qreTemplateOfQre: {
        migrationStrategies: {},
        schema: qreTemplateOfQreSchema,
      },
      questionnaireFieldTemplate: {
        migrationStrategies: {},
        schema: questionnaireFieldTemplateSchema,
      },
      questionnaireTemplate: {
        migrationStrategies: {},
        schema: questionnaireTemplateSchema,
      },
    });

    return true;
  } catch (error) {
    rxdbError(`Error initializing collections: ${error}`);
    return false;
  }
}

/**
 * Purges the data of the collections.
 *
 * @returns {Promise<boolean>} - True if the data was purged, false if an error occurred.
 */
export async function purgeCollectionsData(): Promise<boolean> {
  try {
    const db = await getNoodleDb();

    const collections: RxNoodleDBCollectionsKeys[] = ['qreFieldTemplate', 'qreTemplateOfQre'];

    await Promise.all(
      collections.map(async collection => {
        await db[collection].find().remove();
        await db[collection].cleanup(0);
      }),
    );

    return true;
  } catch (error) {
    rxdbError(`Error purging collections data: ${error}`);
    return false;
  }
}

function buildDBName({ creatorId, userId, currentSuffixNumber }: { creatorId: string; userId: string; currentSuffixNumber: string }): string {
  return `${RXDB_PREFIX}-${creatorId}-${userId}-${currentSuffixNumber}`;
}

function shouldResetRxNoodleDB(): boolean {
  try {
    const hasBeenReset = localStorage.getItem(RESET_DB_KEY);
    const shouldReset = hasBeenReset !== CURRENT_RESET_NUMBER;
    return shouldReset;
  } catch (error) {
    rxdbError(`Error checking reset status: ${error}`);
    return false;
  }
}

function getCurrentRxDBSuffixNumber(): string {
  let currentSuffixNumber = localStorage.getItem(CURRENT_RXDB_SUFFIX_KEY);

  if (!currentSuffixNumber) {
    currentSuffixNumber = '1';
    localStorage.setItem(CURRENT_RXDB_SUFFIX_KEY, currentSuffixNumber);
  }

  return currentSuffixNumber;
}

function incrementCurrentRxDBSuffixNumber(): string {
  const currentSuffixNumber = getCurrentRxDBSuffixNumber();
  const newSuffixNumber = (Number(currentSuffixNumber) + 1).toString();

  localStorage.setItem(CURRENT_RXDB_SUFFIX_KEY, newSuffixNumber);

  return newSuffixNumber;
}

function checkCurrentRxDBStatusAndGetSuffixNumber({ creatorId, userId }: { creatorId: string; userId: string }): string | null {
  try {
    let currentSuffixNumber = getCurrentRxDBSuffixNumber();

    // Check if the database should be reset
    const shouldReset = shouldResetRxNoodleDB();
    if (shouldReset) {
      currentSuffixNumber = incrementCurrentRxDBSuffixNumber();
      markRxNoodleDBAsReset();

      removeAllRxDBs({ avoidRemoveFor: { creatorId, currentSuffixNumber, userId }, wait: 40000 });
    }

    // Return the up to date suffix number
    return currentSuffixNumber;
  } catch (error) {
    rxdbError(`Error checking current RxDB status: ${error}`);
    return null;
  }
}

function markRxNoodleDBAsReset(): void {
  try {
    localStorage.setItem(RESET_DB_KEY, CURRENT_RESET_NUMBER);
  } catch (error) {
    rxdbError(`Error marking reset status: ${error}`);
  }
}

type InitializationConfig = {
  creatorId: string;
  userId: string;
};

/**
 * Initializes the RxNoodleDB instance.
 *
 * @returns {Promise<RxNoodleDB>} - The initialized RxNoodleDB instance.
 */
async function initializeRxNoodleDB({ creatorId, userId }: InitializationConfig): Promise<RxNoodleDB> {
  try {
    const currentSuffixNumber = checkCurrentRxDBStatusAndGetSuffixNumber({ creatorId, userId });

    if (!currentSuffixNumber) {
      throw new Error('Failed to get current RxDB suffix number');
    }

    const dbName = buildDBName({ creatorId, currentSuffixNumber, userId });

    rxdbLog(`Initializing database: ${dbName}`);
    const db = await createRxDatabase<RxNoodleCollections>({
      cleanupPolicy: {
        minimumDeletedTime: 0,
      },
      localDocuments: true,
      name: dbName,
      storage: getRxStorage(),
    });

    rxdbLog(`Database initialized: ${dbName}`);
    rxdbLog('Initializing collections');

    const successfullyAddedCollections = await initializeCollections(db);
    if (!successfullyAddedCollections) throw new Error('Failed to initialize collections');

    rxdbLog('Collections initialized');

    return db;
  } catch (error) {
    rxdbError(`Error initializing RxNoodleDB ${error}`);
    throw error;
  }
}

let rxNoodleDBPromise: Promise<RxNoodleDB> | null = null;

/**
 * Retrieves the RxNoodleDB instance.
 *
 * @returns {Promise<RxNoodleDB>} - The RxNoodleDB instance.
 */
export function getNoodleDb(initConfig?: InitializationConfig): Promise<RxNoodleDB> {
  if (!rxNoodleDBPromise && initConfig) {
    rxNoodleDBPromise = initializeRxNoodleDB(initConfig);
  } else if (!rxNoodleDBPromise) {
    throw new Error('The rxdb database is not initialized and no initialization config was provided');
  }

  return rxNoodleDBPromise;
}

/**
 * Closes the RxNoodleDB instance and set the promise to null.
 * Used when the creator ID changes.
 */
export async function closeRxNoodleDB(): Promise<void> {
  if (rxNoodleDBPromise) {
    const db = await rxNoodleDBPromise;
    rxdbLog(`Closing database: ${db.name}`);
    await db.close();
    rxdbLog(`Database closed: ${db.name}`);
  }

  rxNoodleDBPromise = null;
}

/**
 * Removes a specific RxNoodleDB database.
 *
 * @param {string} name - The name of the database to remove.
 */
async function removeRxNoodleDB(name: string): Promise<void> {
  try {
    const currentDb = await rxNoodleDBPromise;

    if (currentDb?.name === name) {
      await closeRxNoodleDB();
    }

    await removeRxDatabase(name, getRxStorage());
    window.indexedDB.deleteDatabase(name);
  } catch (error) {
    rxdbError(`Error removing RxNoodleDB ${error}`);
    throw error;
  }
}

/**
 * Removes all RxNoodleDB databases.
 * All databases which start with the prefix 'RXDB_PREFIX' will be removed.
 */
async function removeAllRxDBs({
  avoidRemoveFor: { creatorId, userId, currentSuffixNumber } = {},
  wait = 0,
}: {
  avoidRemoveFor?: {
    creatorId?: string;
    userId?: string;
    currentSuffixNumber?: string;
  };
  wait?: number;
} = {}): Promise<void> {
  if (!('databases' in window.indexedDB)) {
    return;
  }

  if (wait) {
    await new Promise(resolve => {
      setTimeout(() => {
        resolve(true);
      }, wait);
    });
  }

  const dbs = await window.indexedDB.databases();

  await Bluebird.map(dbs, async db => {
    const { name } = db;

    rxdbLog(`Checking if database should be removed: ${name}`);

    const isGladeDB = name?.startsWith(RXDB_PREFIX) || name?.startsWith(PREVIOUS_RXDB_PREFIX);
    if (!name || !isGladeDB) {
      rxdbLog(`Skipping removal of non-glade database: ${name}`);
      return;
    }

    if (creatorId && userId && currentSuffixNumber) {
      const isCurrentDB = name === buildDBName({ creatorId, currentSuffixNumber, userId });
      if (isCurrentDB) {
        rxdbLog(`Skipping removal of current database: ${name}`);
        return;
      }
    }

    rxdbLog(`Removing database: ${name}`);
    await removeRxNoodleDB(name);
    rxdbLog(`Database removed: ${name}`);
  });
}

/**
 * Handles the RxDB post logout user process.
 * This function will remove all RxDB databases and increment the current RxDB suffix number.
 * It's used when the user logs out.
 *
 * @returns {Promise<void>} - A promise that resolves when the RxDB post logout user process is complete.
 */
export async function handleRxDBPostLogoutUserProcess(): Promise<void> {
  try {
    incrementCurrentRxDBSuffixNumber();
    await removeAllRxDBs();
  } catch (error) {
    rxdbError(`Error handling RxDB post logout user process ${error}`);
    throw error;
  }
}
